import { createCartEngine } from "@fripick/cart-utils";
import { accountTypes, paymentMethods } from "../constants";
import { parseAmount } from "./parseAmount";

const createEngine = () => {
  let items = [];
  let promotion = null;
  return createCartEngine({
    channel: "pos",
    store: {
      getCartItems: () => items,
      getAppliedPromotion: () => promotion,
      setCartItems: (nextItems) => {
        items = nextItems;
      },
      setAppliedPromotion: (nextPromotion) => {
        promotion = nextPromotion;
      },
    },
  });
};

const policyEngine = createEngine();

const toPosAccountType = (accountTypeId) => {
  if (accountTypeId === accountTypes.employee) return "employee";
  if (accountTypeId === accountTypes.company) return "company";
  throw new Error(`Unsupported POS account type: ${accountTypeId}`);
};

const toPosTender = (paymentMethod) => {
  if (paymentMethod === paymentMethods.nomina) return "payroll";
  if (paymentMethod === paymentMethods.efectivo) return "cash";
  if (paymentMethod === paymentMethods.tarjeta) return "card";
  throw new Error(`Unsupported POS payment method: ${paymentMethod}`);
};

const toPosSecondaryTender = (paymentMethod) => {
  if (paymentMethod == null) return undefined;
  if (paymentMethod === paymentMethods.efectivo) return "cash";
  if (paymentMethod === paymentMethods.tarjeta) return "card";
  throw new Error(`Unsupported POS secondary payment method: ${paymentMethod}`);
};

const toBoolean = (value) =>
  value === true || value === 1 || value === "true" || value === "1";

export const parsePosCurrentDate = (currentDateTime) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(currentDateTime ?? "");
  if (!match) return null;

  const [, year, month, day] = match;
  const date = new Date(Number(year), Number(month) - 1, Number(day), 12);
  return date.getFullYear() === Number(year) &&
    date.getMonth() === Number(month) - 1 &&
    date.getDate() === Number(day)
    ? date
    : null;
};

const toSharedSections = (sections = []) =>
  sections.map((section) => ({
    additional: toBoolean(section.additional),
    id: Number(section.variantId ?? section.id ?? 0),
    name: section.name ?? "",
    price: String(section.price ?? 0),
    priceForCompanyBranches: section.priceForCompanyBranches ?? [],
    sectionId: Number(section.sectionId ?? section.id ?? 0),
  }));

const toSharedItem = (product, sourceIndex) => {
  const unitPrice = Number(
    parseAmount(product.unitPrice ?? product.price) ?? 0,
  );
  const quantity = Number(product.quantity ?? 0);
  return {
    appliedBenefits: {
      assignment: 0,
      benefits: [],
      dailyAssignment: 0,
      giftCardAmounts: [],
      giftCards: [],
      promotionAmount: 0,
      promotions: [],
      subsidy: 0,
      usedCreditLimit: 0,
    },
    applyServiceTax: toBoolean(product.applyServiceTax),
    basePrice: unitPrice * quantity,
    catalogId: Number(product.catalogId ?? product.catalogIdAsInt ?? 0),
    code: product.sku ?? product.code ?? String(product.id ?? sourceIndex),
    combo: Boolean(product.combo),
    deliveryDate: product.deliveryDate ?? "",
    deliveryTypeId: Number(product.deliveryTypeId ?? 0),
    hasVariant: Boolean(product.hasVariant),
    id: Number(product.id),
    index: sourceIndex,
    name: product.name ?? "",
    price: unitPrice * quantity,
    providerId: Number(product.providerId ?? 0),
    providerName: product.providerName ?? "",
    quantity,
    sections: toSharedSections(product.sections),
    serviceTaxesAmount: 0,
    taxesAmount: 0,
    taxesAmountList: [],
    taxTypes: (product.taxTypes ?? []).map(String),
    unitPrice,
  };
};

const normalizeGiftCards = (giftcardResponse) => {
  const response = giftcardResponse?.data ?? {};
  return (response.giftCards ?? []).map((giftCard) => ({
    ...giftCard,
    id: Number(giftCard.id),
    amount: String(giftCard.amount),
    applyAfterSubsidy: toBoolean(
      response.applyAfterSubsidy ?? giftCard.applyAfterSubsidy,
    ),
    expiredDate: giftCard.expiredDate ?? giftCard.expiredDateWithoutFormat,
    expiredDateWithoutFormat:
      giftCard.expiredDateWithoutFormat ?? giftCard.expiredDate,
    userId: Number(giftCard.userId ?? 0),
  }));
};

const normalizePromotion = (promotion) =>
  promotion
    ? {
        ...promotion,
        itemId: promotion.itemId == null ? null : Number(promotion.itemId),
        maxPurchaseValue:
          promotion.maxPurchaseValue == null
            ? null
            : Number(promotion.maxPurchaseValue),
        minPurchaseValue:
          promotion.minPurchaseValue == null
            ? null
            : Number(promotion.minPurchaseValue),
        promoId: Number(promotion.promoId),
        promoLimitPerPerson:
          promotion.promoLimitPerPerson == null
            ? null
            : Number(promotion.promoLimitPerPerson),
        providerId:
          promotion.providerId == null ? null : Number(promotion.providerId),
        usage: promotion.usage == null ? null : Number(promotion.usage),
      }
    : null;

const toDisplayProduct = (products, line) => {
  const sourceIndex = line.sourceIndex;
  const source = products[sourceIndex];
  if (!source)
    throw new Error(`Missing POS source product at index ${sourceIndex}`);

  const promotion = line.item.appliedBenefits.promotion ?? null;
  const checkoutTotals = {
    ...line.totals,
    taxesList: line.item.taxesAmountList,
  };
  return {
    ...source,
    appliedPromotionId:
      checkoutTotals.totalPromotion > 0 ? (promotion?.promoId ?? null) : null,
    applyServiceTax: line.item.applyServiceTax,
    benefits: checkoutTotals.totalBenefits,
    calculatedItem: line.item,
    cartIndex: line.cartIndex,
    catalogId: line.item.catalogId,
    combo: line.item.combo,
    cashOrCardValue: line.payment.externalAmount,
    checkoutTotals,
    discountAmount: checkoutTotals.totalPromotion,
    externalAmount: line.payment.externalAmount,
    deliveryDate: line.item.deliveryDate,
    deliveryTypeId: line.item.deliveryTypeId,
    id: line.item.id,
    name: line.item.name,
    payment: line.payment,
    promotion,
    providerId: line.item.providerId,
    quantity: line.item.quantity,
    sections: source.sections ?? [],
    sourceIndex,
    taxesAmountList: line.item.taxesAmountList,
    taxTypes: line.item.taxTypes,
    unitPrice: line.item.unitPrice,
  };
};

export const calculatePosCheckout = ({
  accountTypeId,
  applicableBenefits,
  asOf,
  availableBalance,
  benefits,
  currentDateTime,
  giftcardResponse,
  paymentMethod,
  products,
  selectedGlobalPromotion = null,
}) => {
  const checkout = createEngine().calculateCheckout({
    accountType: toPosAccountType(accountTypeId),
    applicability: applicableBenefits ?? {},
    asOf: asOf ?? parsePosCurrentDate(currentDateTime) ?? new Date(),
    availableBalance: Number(availableBalance ?? 0),
    giftCards: normalizeGiftCards(giftcardResponse),
    items: products.map((product, sourceIndex) => ({
      item: toSharedItem(product, sourceIndex),
      selectedPromotion: normalizePromotion(product.promotion),
      sourceIndex,
    })),
    primaryTender: toPosTender(paymentMethod),
    rawBenefits: benefits ?? [],
    selectedGlobalPromotion: normalizePromotion(selectedGlobalPromotion),
  });

  return {
    ...checkout,
    displayProducts: checkout.lines.map((line) =>
      toDisplayProduct(products, line),
    ),
  };
};

export const validatePosTender = ({
  amountToProcess,
  cashOrCardMethod,
  cashOrCardValue,
  cashValue,
  selectedPaymentMethod,
}) => {
  const primaryTender = toPosTender(selectedPaymentMethod);
  const input =
    primaryTender === "payroll"
      ? {
          primaryAmount: amountToProcess,
          primaryTender,
          receivedCash: cashValue,
          secondaryAmount: cashOrCardValue,
          secondaryTender: toPosSecondaryTender(cashOrCardMethod),
        }
      : {
          primaryAmount: amountToProcess,
          primaryTender,
          receivedCash: cashValue,
        };
  return policyEngine.validateTender(input);
};
